import React, { useState } from "react";
import PropTypes from "prop-types";
import { Button, makeStyles, Text } from "@fluentui/react-components";
import { Warning24Filled } from "@fluentui/react-icons";
import { Modal } from "../modal/Modal";

const useStyles = makeStyles({
  dialogContent: {
    display: "flex",
    flexDirection: "column",
    gap: "16px"
  },
  warningContainer: {
    display: "flex",
    alignItems: "flex-start",
    gap: "12px",
    padding: "12px",
    backgroundColor: "rgba(242, 153, 74, 0.1)",
    borderRadius: "4px",
    border: "1px solid rgba(242, 153, 74, 0.3)"
  },
  warningIcon: {
    flexShrink: 0,
    color: "#F2994A"
  },
  warningText: {
    color: "rgba(255, 255, 255, 0.9)",
    lineHeight: "20px",
    fontSize: "14px"
  },
  message: {
    color: "rgba(255, 255, 255, 0.7)",
    fontSize: "14px",
    lineHeight: "20px"
  },
  errorContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    padding: "12px",
    backgroundColor: "color-mix(in srgb, var(--color-danger) 10%, transparent)",
    borderRadius: "4px",
    border: "1px solid color-mix(in srgb, var(--color-danger) 30%, transparent)"
  },
  errorText: {
    color: "var(--color-danger)",
    fontSize: "14px"
  },
  actions: {
    display: "flex",
    gap: "12px"
  }
});

const confirmActionLabels = {
  copy: {
    idle: "Create a Copy",
    loading: "Creating Copy..."
  },
  replace: {
    idle: "Optimize Original",
    loading: "Optimizing..."
  }
};

export function ConfirmOptimizationDialog({ fileName, onConfirmReplace, onConfirmCopy, onCancel, hideDialog }) {
  const styles = useStyles();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [loadingAction, setLoadingAction] = useState(null);

  const handleClose = () => {
    if (!isLoading) {
      onCancel?.();
      hideDialog?.();
    }
  };

  const handleCopy = async () => {
    setIsLoading(true);
    setLoadingAction("copy");
    setError(null);

    try {
      await onConfirmCopy?.();
      hideDialog?.();
    } catch (err) {
      console.error("Failed to create copy:", err);
      setError(err.message || "Failed to create optimized copy. Please try again.");
      setIsLoading(false);
      setLoadingAction(null);
    }
  };

  const handleReplace = async () => {
    setIsLoading(true);
    setLoadingAction("replace");
    setError(null);

    try {
      await onConfirmReplace?.();
      hideDialog?.();
    } catch (err) {
      console.error("Failed to replace original:", err);
      setError(err.message || "Failed to optimize original. Please try again.");
      setIsLoading(false);
      setLoadingAction(null);
    }
  };

  return (
    <Modal
      title="Confirm Optimization"
      open={true}
      onOpenChange={handleClose}
      onClose={handleClose}
      contentClassName={styles.dialogContent}
      actions={
        <div className={styles.actions}>
          <Button
            appearance="secondary"
            onClick={handleCopy}
            disabled={isLoading}
            aria-label="Create an optimized copy"
          >
            {loadingAction === "copy" ? confirmActionLabels.copy.loading : confirmActionLabels.copy.idle}
          </Button>
          <Button
            appearance="primary"
            onClick={handleReplace}
            disabled={isLoading}
            aria-label="Replace with optimized model"
          >
            {loadingAction === "replace" ? confirmActionLabels.replace.loading : confirmActionLabels.replace.idle}
          </Button>
        </div>
      }
    >
      <Text className={styles.message}>
        Applying this optimization will replace the current asset file. Some features may be lost and the asset may fail
        to load correctly.
      </Text>

      <div className={styles.warningContainer}>
        <Warning24Filled className={styles.warningIcon} />
        <Text className={styles.warningText}>
          <Text weight="semibold">Warning:</Text> The original file “{fileName}” will be permanently replaced with the
          optimized version.
        </Text>
      </div>

      {error && (
        <div className={styles.errorContainer}>
          <Text className={styles.errorText}>{error}</Text>
        </div>
      )}
    </Modal>
  );
}

ConfirmOptimizationDialog.propTypes = {
  fileName: PropTypes.string.isRequired,
  onConfirmReplace: PropTypes.func.isRequired,
  onConfirmCopy: PropTypes.func.isRequired,
  onCancel: PropTypes.func,
  hideDialog: PropTypes.func
};

export default ConfirmOptimizationDialog;
